import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const ProjectsSection = () => {
  const sectionRef = useRef(null);
  const mobileRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 75%", "center center"]
  });

  // Mobile scroll progress
  const { scrollYProgress: mobileScrollProgress } = useScroll({
    target: mobileRef,
    offset: ["start 100%", "end 20%"]
  });

  // Create transforms for each website position (desktop only)
  const transforms = {
    topLeft: {
      x: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '-110%']),
      y: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '-115%']),
      rotate: useTransform(scrollYProgress, [0.1, 0.9], [0, -10])
    },
    topRight: {
      x: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '10%']),
      y: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '-115%']),
      rotate: useTransform(scrollYProgress, [0.1, 0.9], [0, 10])
    },
    bottomLeft: {
      x: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '-110%']),
      y: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '15%']),
      rotate: useTransform(scrollYProgress, [0.1, 0.9], [0, 10])
    },
    bottomRight: {
      x: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '10%']),
      y: useTransform(scrollYProgress, [0.1, 0.9], ['-50%', '15%']),
      rotate: useTransform(scrollYProgress, [0.1, 0.9], [0, -10])
    }
  };

  // Mobile transforms - create all transforms at top level - fine-tuned delays
  const mobileTransforms = [
    // Project 0 - slides from right
    {
      x: useTransform(mobileScrollProgress, [0, 0.25], ['100%', '0%']),
      opacity: useTransform(mobileScrollProgress, [0, 0.25], [0, 1]),
      scale: useTransform(mobileScrollProgress, [0, 0.25], [0.8, 1])
    },
    // Project 1 - slides from left
    {
      x: useTransform(mobileScrollProgress, [0.15, 0.4], ['-100%', '0%']),
      opacity: useTransform(mobileScrollProgress, [0.15, 0.4], [0, 1]),
      scale: useTransform(mobileScrollProgress, [0.15, 0.4], [0.8, 1])
    },
    // Project 2 - slides from right
    {
      x: useTransform(mobileScrollProgress, [0.35, 0.6], ['100%', '0%']),
      opacity: useTransform(mobileScrollProgress, [0.35, 0.6], [0, 1]),
      scale: useTransform(mobileScrollProgress, [0.35, 0.6], [0.8, 1])
    },
    // Project 3 - slides from left
    {
      x: useTransform(mobileScrollProgress, [0.55, 0.8], ['-100%', '0%']),
      opacity: useTransform(mobileScrollProgress, [0.55, 0.8], [0, 1]),
      scale: useTransform(mobileScrollProgress, [0.55, 0.8], [0.8, 1])
    }
  ];

  const scale = useTransform(scrollYProgress, [0.1, 0.5], [0.8, 1]);
  const opacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);
  // Slower text animation
  const textOpacity = useTransform(scrollYProgress, [0.5, 0.85], [0, 1]);
  const textY = useTransform(scrollYProgress, [0.5, 0.85], [20, 0]);

  // Mobile text animation - starts earlier too
  const mobileTextOpacity = useTransform(mobileScrollProgress, [0, 0.15], [0, 1]);
  const mobileTextY = useTransform(mobileScrollProgress, [0, 0.15], [30, 0]);

  const websites = [
    {
      id: 1,
      desktopImage: '/projects/desktop/rega-shel-or.png',
      mobileImage: '/projects/mobile/rega-mobile.jpg',
      title: 'רגע של אור',
      description: 'עמותה למען מטופלים בבתי החולים',
      url: 'https://rega-shel-or.web.app/',
      transforms: transforms.topLeft
    },
          {
        id: 2,
        desktopImage: '/projects/desktop/galit-retreat.png',
        mobileImage: '/projects/mobile/galit-mobile.jpg',
        title: 'Galit Retreat',
        description: 'נסיגת רווחה וריפוי',
        url: 'https://galit-retreat.web.app/',
        transforms: transforms.topRight
      },
    {
      id: 3,
      desktopImage: '/projects/desktop/keren.png',
      mobileImage: '/projects/mobile/keren-mobile.jpg',
      title: 'קרן קמרה וורקס',
      description: 'הדרכת משחק מול מצלמה',
      url: 'https://keren-cameraworks.web.app',
      transforms: transforms.bottomLeft
    },
    {
      id: 4,
      desktopImage: '/projects/desktop/stroopy.png',
      mobileImage: '/projects/mobile/stroopy-mobile.jpg',
      title: 'Stroopy',
      description: 'הסטרופ וופל הראשון בישראל',
      url: 'https://stroopy-ccc16.web.app/',
      transforms: transforms.bottomRight
    }
  ];

  return (
    <section id="projects" ref={sectionRef} className="relative min-h-[100vh] w-full overflow-visible">
      {/* Desktop Version */}
      <div className="hidden lg:block sticky top-0 h-screen w-full">
        <div className="relative flex h-full w-full items-center justify-center">
          {/* Container for both images and text */}
          <div className="relative flex aspect-square w-[800px] items-center justify-center">
            {/* Website Images */}
            {websites.map((website, index) => (
              website.url ? (
                <motion.div
                  key={website.id}
                  className="absolute w-[440px] xl:w-[560px] 2xl:w-[740px] group"
                  style={{
                    left: '50%',
                    top: '50%',
                    x: website.transforms.x,
                    y: website.transforms.y,
                    rotate: website.transforms.rotate,
                    scale,
                    opacity,
                    zIndex: 4 - index,
                    transformOrigin: 'center',
                    padding: '20px'
                  }}
                >
                  <a
                    href={website.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full cursor-pointer"
                  >
                    <div className="relative w-full h-full">
                      <img
                        src={website.desktopImage}
                        alt={website.title}
                        className="rounded-xl shadow-2xl w-full h-[400px] object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center">
                        <div className="text-center text-white">
                          <h3 className="text-2xl font-bold mb-2">{website.title}</h3>
                          <p className="text-lg opacity-90">{website.description}</p>
                        </div>
                      </div>
                    </div>
                  </a>
                </motion.div>
              ) : (
                <motion.div
                  key={website.id}
                  className="absolute w-[440px] xl:w-[560px] 2xl:w-[740px] group cursor-pointer"
                  style={{
                    left: '50%',
                    top: '50%',
                    x: website.transforms.x,
                    y: website.transforms.y,
                    rotate: website.transforms.rotate,
                    scale,
                    opacity,
                    zIndex: 4 - index,
                    transformOrigin: 'center',
                    padding: '20px'
                  }}
                >
                  <div className="relative w-full h-full">
                    <img
                      src={website.desktopImage}
                      alt={website.title}
                      className="rounded-xl shadow-2xl w-full h-[400px] object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center">
                      <div className="text-center text-white">
                        <h3 className="text-2xl font-bold mb-2">{website.title}</h3>
                        <p className="text-lg opacity-90">{website.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            ))}

            {/* Text Content */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: textOpacity,
                y: textY,
                zIndex: 5,
                pointerEvents: 'none'
              }}
            >
              <div className="w-full max-w-3xl px-4 text-center" style={{ direction: 'rtl' }}>
                <h2 className="mb-4 text-4xl font-bold xl:text-5xl 2xl:text-6xl">
                  כש<span className="text-pink-400">AI</span> בונה את האתר שלך
                </h2>
                <p className="text-2xl xl:text-3xl 2xl:text-4xl">
                  מהר יותר, פשוט יותר, <span className="font-bold text-pink-400">זול יותר</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Mobile Version */}
      <div ref={mobileRef} className="lg:hidden section-padding min-h-[150vh] relative overflow-x-hidden">
        <div className="container mx-auto px-4 overflow-x-hidden">
          {/* Text Content First */}
          <motion.div 
            className="text-center mb-12" 
            style={{ 
              direction: 'rtl',
              opacity: mobileTextOpacity,
              y: mobileTextY
            }}
          >
            <h2 className="mb-4 text-3xl font-bold sm:text-4xl md:text-5xl">
              כש<span className="text-pink-400">AI</span> בונה את האתר שלך
            </h2>
            <p className="text-xl sm:text-2xl md:text-3xl text-gray-300">
              מהר יותר, פשוט יותר, <span className="font-bold text-pink-400">זול יותר</span>
            </p>
          </motion.div>

          {/* Projects - Sequential Animation */}
          <div className="space-y-8 sm:space-y-12 max-w-4xl mx-auto overflow-x-hidden">
            {websites.map((website, index) => {
              return (
                <div key={website.id} className="w-full overflow-x-hidden">
                  <motion.div
                    className="flex justify-center"
                    style={{
                      x: mobileTransforms[index].x,
                      opacity: mobileTransforms[index].opacity,
                      scale: mobileTransforms[index].scale
                    }}
                  >
                    <div className="w-full max-w-xs sm:max-w-sm group">
                      {website.url ? (
                        <a
                          href={website.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full h-full cursor-pointer"
                        >
                          <div className="relative w-full h-[600px] sm:h-[700px] rounded-3xl overflow-hidden shadow-2xl">
                            <img
                              src={website.mobileImage}
                              alt={website.title}
                              className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl flex items-center justify-center">
                              <div className="text-center text-white px-4">
                                <h3 className="text-xl sm:text-2xl font-bold mb-2">{website.title}</h3>
                                <p className="text-sm sm:text-base opacity-90">{website.description}</p>
                              </div>
                            </div>
                          </div>
                        </a>
                      ) : (
                        <div className="relative w-full h-[600px] sm:h-[700px] rounded-3xl overflow-hidden shadow-2xl cursor-pointer">
                          <img
                            src={website.mobileImage}
                            alt={website.title}
                            className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl flex items-center justify-center">
                            <div className="text-center text-white px-4">
                              <h3 className="text-xl sm:text-2xl font-bold mb-2">{website.title}</h3>
                              <p className="text-sm sm:text-base opacity-90">{website.description}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection; 